import * as React from "react";
import { v4 as uuid } from "uuid";
import { withDefault, hasOwnProperty } from "collection-utils";
import { withTheme } from "styled-components";

import {
    BuyScreenWrapper,
    Name,
    Price,
    InputWrapper,
    FormContainer,
    StripeWrapper,
    FormHeader,
    ButtonArea,
    ButtonWrapper,
    Total,
    Spacer,
    HeaderArea,
    Description,
    HeaderImage,
    ConfirmationMessage,
    StyledInput,
    inputTextSize,
    AuthFrame,
    StripeLogo,
    StripeIcon,
    InfoWrapper,
} from "./buy-screen-style";
import ActivitySpinner from "../../../chrome/views/activity-spinner/activity-spinner";
import { stripeClientForConnect, stripeClientForPublishableKey } from "../../../lib/stripe-api";
import { RuntimeTheme } from "../../../lib/theme/theme";
import Button from "../button/button";
import { Appearance } from "common/generator/components/button-appearance";
import { logError, logInfo } from "common/debug-print";
import { getLocalizedString, formatLocalizedString } from "common/components/localization";
import {
    isPaymentClientActionRequiredResult,
    isPaymentFailureCardErrorResult,
    PaymentClientActionRequiredResult,
    isPaymentFailureCardDeclinedErrorResult,
    isPaymentFailureInsufficientCharge,
} from "common/in-app-purchases";
import { AppEnvironment } from "common/components/types";
import { disableBrowserAutocompleteToken, defined, sleep } from "common/Support";
import { systemFontFamily } from "common/BaseTheme";
import { StripeInAppPurchaseConfirmIntentBody } from "common/firebase-function-types";
import {
    BuyScreenTransactionBootstrap,
    convertBuyScreenTransactionToRequestedInAppPurchaseV2,
    totalPriceForItems,
} from "../../../lib/buy-screen";
import { formatPrice } from "common/generator/components/buy-button-defaults";
import { trackEvent } from "../../../../analytics";
import { getFeatureSetting } from "common/feature-settings";
import * as routes from "../../../../routes";
import { PaymentInformation } from "common/Database";
import { callCloudFunctionWeb } from "sharedUI/lib/function-utils";

interface Props {
    theme: RuntimeTheme;
    basis: BuyScreenTransactionBootstrap;
    stripePaymentInformation: PaymentInformation;
    appEnvironment: AppEnvironment;
    onFinished: (() => void) | undefined;
}

type ScreenState = "input" | "capturing" | "submitting" | "auth-start" | "auth-confirm" | "failure" | "success";

interface State {
    stripeSDK: any | undefined;
    testMode: boolean;
    idempotencyKey: string;
    card: any | undefined;
    cardIsReady: boolean;
    name: string;
    email: string;
    address_line1: string;
    address_line2: string;
    address_city: string;
    address_state: string;
    address_country: string;
    address_zip: string;
    phone: string;
    billingZip: string;
    screenState: ScreenState;
    failureMessage: string;
    tdsPayload?: PaymentClientActionRequiredResult;
}

// FIXME: Extract this into a library
function LinkNewWindow({ href, children }: { href: string; children: React.ReactNode }): JSX.Element {
    return (
        <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    );
}

class BuyScreenWithTheme extends React.PureComponent<Props, State> {
    private billZipRef = React.createRef<any>();

    public state: State = {
        stripeSDK: undefined,
        testMode: false,
        idempotencyKey: "",
        card: undefined,
        cardIsReady: false,
        name: "",
        email: "",
        address_line1: "",
        address_line2: "",
        address_city: "",
        address_state: "",
        address_country: "",
        address_zip: "",
        billingZip: "",
        phone: "",
        screenState: "input",
        failureMessage: "",
    };

    private cardOnChange = (event: any) => {
        if (event.complete) {
            this.setState({ cardIsReady: true });
        } else {
            this.setState({ cardIsReady: false });
        }
    };

    private async createStripeSDK(): Promise<any> {
        const { stripePaymentInformation } = this.props;
        const { stripeUserID, livePublishableKey, testPublishableKey } = stripePaymentInformation;
        let { stripeSDK, testMode } = this.state;
        if (stripeSDK !== undefined) return stripeSDK;

        // Note that testPublishableKey and livePublishableKey are only ever passsed
        // in the builder, never the player.
        if (testPublishableKey !== undefined && testPublishableKey !== livePublishableKey) {
            // Historically, we've always saved testPublishableKey === livePublishableKey.
            // If they are the same, that means we aren't in test mode.
            stripeSDK = await stripeClientForPublishableKey(testPublishableKey);
            testMode = true;
        } else {
            stripeSDK = await stripeClientForConnect(stripeUserID);
            testMode = false;
        }

        this.setState({ stripeSDK, testMode });
        return stripeSDK;
    }

    private async createStripeCard(): Promise<void> {
        const { theme } = this.props;
        const stripeSDK = await this.createStripeSDK();
        // Stripe expects us to use non-computed CSS for Elements.
        // Thankfully! We get to set CSS here, too.
        const card = stripeSDK.elements().create("card", {
            style: {
                base: {
                    fontSize: inputTextSize,
                    fontFamily: systemFontFamily,
                    color: theme.fgColorDark,
                    ":-webkit-autofill": {
                        fontSize: inputTextSize,
                        fontFamily: systemFontFamily,
                        color: theme.fgColorDark,
                    },
                },
            },
            hidePostalCode: true,
        });
        card.on("change", this.cardOnChange);
        card.mount("#cardWrapper");
        // Whenever we create a new card, we treat the billing zip as part of the card.
        this.setState({ stripeSDK, card, idempotencyKey: uuid(), cardIsReady: false, billingZip: "" });
    }

    private async stripePaymentIntentStatus(intentClientSecret: string): Promise<string> {
        const { stripeSDK } = this.state;
        const paymentIntentResponse = await defined(stripeSDK).retrievePaymentIntent(intentClientSecret);
        if (hasOwnProperty(paymentIntentResponse, "error")) {
            throw paymentIntentResponse.error;
        }
        if (!hasOwnProperty(paymentIntentResponse, "paymentIntent")) {
            const message = "Unexpected Stripe SDK return for retrievePaymentInformation";
            logError(message);
            logError(paymentIntentResponse);
            // FIXME: This should throw an error
            // eslint-disable-next-line
            throw { message, value: paymentIntentResponse };
        }
        const { paymentIntent } = paymentIntentResponse;
        const { status } = paymentIntent as any;
        return status;
    }

    private async dispatchPaymentCloudFunctionState(
        func: string,
        body: any
    ): Promise<{ success: boolean; screenState: ScreenState; failureMessage: string }> {
        let failureMessage = getLocalizedString("catchallPaymentError");
        const purchaseResponse = await callCloudFunctionWeb(func, body, {});
        if (purchaseResponse === undefined) return { success: false, screenState: "failure", failureMessage };

        switch (purchaseResponse.status) {
            case 200:
                const payload = await purchaseResponse.json();
                // We don't expect this much in the States... but 3-D Secure is a thing.
                // And that's something we handle out here in the client.
                if (isPaymentClientActionRequiredResult(payload)) {
                    this.setState({ tdsPayload: payload });
                    return { success: false, screenState: "auth-start", failureMessage };
                } else {
                    return { success: true, screenState: "success", failureMessage };
                }
            case 402:
                // When this happens, you get back a nifty { message: string, type: string }
                // that'll tell you what's up.
                const typedResponse = await purchaseResponse.json();
                const { message, type, kind } = typedResponse;
                logError(`${type} (${kind}): ${message}`);
                // if `kind` is a card error then we should communicate this to the user
                if (isPaymentFailureCardErrorResult(typedResponse)) {
                    failureMessage = getLocalizedString("cardInfoRejected");
                } else if (isPaymentFailureCardDeclinedErrorResult(typedResponse)) {
                    failureMessage = getLocalizedString("cardPaymentDeclined");
                } else if (isPaymentFailureInsufficientCharge(typedResponse) && !routes.isPlayer()) {
                    // Note that we only show this message in the builder.
                    // FIXME: Figure out what to display in the player in this case.
                    failureMessage =
                        "Cannot process this payment: Stripe requires a minimum charge of US$0.50 to process payments.";
                }
                break;
            case 404:
                // Something went really wrong and the item no longer exists
                // FIXME: We now know which items went missing out of the cart.
                // We should do something about those items.
                failureMessage = getLocalizedString("purchaseItemHasBeenRemoved");
                break;
            case 410:
                // There's no owner or the integration is incomplete.
                // FIXME: Better error messages here.
                break;
            default:
                logError(`Unexpected status for inAppPurchase: ${purchaseResponse.status}`);
                break;
        }
        return { success: false, screenState: "failure", failureMessage };
    }

    private onMessageHandler = async (message: MessageEvent) => {
        const { data } = message;
        const { appID } = this.props.basis;
        const { screenState: currentScreenState, tdsPayload } = this.state;
        if (currentScreenState !== "auth-start") {
            window.removeEventListener("message", this.onMessageHandler);
            return;
        }
        if (data !== "3ds-auth-complete") return;
        const { processorPayload } = defined(tdsPayload);
        const { intentClientSecret, purchaseID } = processorPayload;
        try {
            this.setState({ screenState: "auth-confirm" });

            let screenState: ScreenState = "failure";
            let failureMessage = getLocalizedString("catchallPaymentError");
            let retries = 3;
            // This is a bit bouncy, but... we have to sometimes confirm the payment intent again, manually.
            // So let's do this in a loop.
            do {
                const status = await this.stripePaymentIntentStatus(intentClientSecret);

                if (status === "succeeded") {
                    screenState = "success";
                    this.trackPurchase();
                    break;
                } else if (status === "requires_action" || status === "requires_source_action") {
                    // We just came from that status! Sometimes there's a race condition where the 3D Secure
                    // redirect happens before Stripe is ready for us. Let's hang out a bit and wait for it
                    // to reconcile.
                    logInfo("Had to wait on Stripe requires_action race");
                    await sleep(500);
                } else if (status === "requires_payment_method" || status === "requires_source") {
                    // We get here if the 3D Secure workflow rejected the payment.
                    // Note that "requires_payment_method" is the status for API versions above 2019-02-11
                    // but we're on 2018-11-08 so the status would be "requires_source" in this case.
                    screenState = "input";
                    break;
                } else if (status === "requires_confirmation") {
                    const body: StripeInAppPurchaseConfirmIntentBody = { appID, purchaseID };
                    const confirmationResult = await this.dispatchPaymentCloudFunctionState(
                        "stripeInAppPurchaseConfirmIntent",
                        body
                    );
                    if (!confirmationResult.success) {
                        screenState = confirmationResult.screenState;
                        failureMessage = confirmationResult.failureMessage;
                        break;
                    }
                } else {
                    logError(`Unexpected paymentIntent status: ${status}`);
                    break;
                }
            } while (retries-- > 0);

            this.setState({ screenState, failureMessage });
        } catch (e: unknown) {
            logError(`Stripe 3D Secure workflow: ${e}`);
            this.setState({ failureMessage: getLocalizedString("catchallPaymentError"), screenState: "failure" });
        } finally {
            window.removeEventListener("message", this.onMessageHandler);
        }
    };

    private trackPurchase() {
        const {
            basis: { items, currency, withShipping, appID },
        } = this.props;

        trackEvent("purchase", {
            app_id: appID,
            value: totalPriceForItems(items),
            tax: 0,
            currency: currency.code,
            shipping: withShipping.toString(),
            items: items.length,
        });
    }

    private createStripe3DSecure(): JSX.Element {
        const { tdsPayload } = this.state;
        const { processorPayload } = defined(tdsPayload);
        const { tdsRedirectURL } = processorPayload;
        window.addEventListener("message", this.onMessageHandler);
        return <AuthFrame src={tdsRedirectURL} />;
    }

    public async componentDidMount() {
        await this.createStripeCard();
        const defaultEmail = this.props.appEnvironment.authenticator.realEmail;

        this.setState({ email: withDefault(defaultEmail, "") });
    }

    private ensureFocusOffStripeElement() {
        // Safari on iOS 12.3.1 and 12.4 seems to lockjaw on the Stripe iframes pretty badly.
        // Even when the DOM elements disappear, the keyboard consistently asserts itself.
        // Amazingly, this doesn't happen for inputs that are present on our own `document`.
        // Let's give Safari a little help and unfocus the iframes that are about to disappear,
        // specifically by focusing the zip in the middle of the transition.
        const zipInput = this.billZipRef.current;
        if (zipInput === null || zipInput === undefined) return;
        zipInput.focus();
        zipInput.blur();
    }

    public getSnapshotBeforeUpdate(_prevProps: Props, prevState: State) {
        const { screenState: prevScreenState } = prevState;
        const { screenState: nextScreenState } = this.state;
        if (prevScreenState === "input" && nextScreenState !== "input") {
            this.ensureFocusOffStripeElement();
        }
        return null;
    }

    public async componentDidUpdate(prevProps: Props, prevState: State) {
        const { theme: prevTheme } = prevProps;
        const { theme: nextTheme } = this.props;
        const { screenState, card: prevCard } = this.state;
        if (
            prevTheme.fgColorDark !== nextTheme.fgColorDark ||
            (screenState === "input" && prevState.screenState !== "input")
        ) {
            // Two cases:
            //
            // 1. The theme changed. This only happens in the builder so we can
            //    just clear out the element entirely.
            // 2. We've transitioned from non-"input" to "input".
            //    Since we're back in an "input" state we need to re-initialize
            //    the card info.
            //
            // Great news! We lose all of it when we come back. This is actually good for
            // UX: if you come back, your payment information was likely wrong anyway.
            await this.createStripeCard();
        } else if (screenState === "capturing") {
            // This will get tossed on failure bounce-back anyway, so this is safe.
            prevCard.disabled = true;
        }
    }

    public componentWillUnmount() {
        this.ensureFocusOffStripeElement();
    }

    private getSanitizedPhoneInput(): string | undefined {
        const { phone } = this.state;
        const sanitized = phone.trim();
        return sanitized.length > 0 ? sanitized : undefined;
    }

    private async onSubmitInfo(): Promise<ScreenState> {
        const {
            card,
            stripeSDK,
            idempotencyKey,
            name,
            address_line1,
            address_line2,
            address_city: city,
            address_state: state,
            address_country: country,
            address_zip: postalCode,
            email,
            billingZip,
            testMode,
        } = this.state;

        if (card === undefined || stripeSDK === undefined) return "input";

        const billingDetails = {
            name,
            address: {
                postal_code: billingZip,
            },
        }; // FIXME: Do we need the full address here too?

        // The result is a { token: { id: "..." } } and a bunch of other things.
        // FIXME: Surely this all belongs somewhere _else_.
        const { basis } = this.props;
        const { withShipping } = basis;
        const shippingAddress = withShipping
            ? {
                  city,
                  country,
                  line1: address_line1,
                  line2: address_line2,
                  state,
                  postalCode,
              }
            : undefined;

        let failureMessage: string = getLocalizedString("catchallPaymentError");
        let screenState: ScreenState = "failure";
        try {
            const result = await stripeSDK.createPaymentMethod("card", card, { billing_details: billingDetails });
            if (result.error !== undefined) {
                const { message, code } = result.error;
                logError(`Error creating payment method for buy button: ${code}: ${message}`);

                // Sometimes, things go wrong in the Stripe SDK over which we have no control.
                // In the builder, we can entice the end-user to do something about it.
                // The case of platform_api_key_expired or account_invalid is simple:
                // they either revoked access to their Stripe account, _or_ they changed
                // their Stripe password.
                //
                // We'll likely need to enable better errors in the future, but this will
                // at least make support requests that much easier to handle.
                if (!routes.isPlayer()) {
                    if (code === "platform_api_key_expired" || code === "account_invalid") {
                        failureMessage =
                            "Your Stripe account has been disconnected from Glide. Please re-connect your Stripe account to continue.";
                    } else {
                        failureMessage = `Stripe responded with an error: ${message}`;
                    }
                }
                this.setState({ failureMessage });
                return "failure";
            }
            if (result.paymentMethod === undefined) {
                logError("Stripe API has become incoherent.");
                logError(result);
                this.setState({ failureMessage });
                return "failure";
            }
            // We'd do this earlier, except the SDK needs that component to be mounted in order to capture details.
            this.setState({ screenState: "submitting" });
            const requestBody = convertBuyScreenTransactionToRequestedInAppPurchaseV2(
                {
                    ...basis,
                    name,
                    email,
                    paymentMethodID: result.paymentMethod.id,
                    idempotencyKey,
                    shippingAddress,
                    shippingPhone: this.getSanitizedPhoneInput(),
                },
                testMode
            );
            const purchaseResponse = await this.dispatchPaymentCloudFunctionState("inAppPurchase", requestBody);
            if (purchaseResponse.success) {
                this.trackPurchase();
            }
            screenState = purchaseResponse.screenState;
            failureMessage = purchaseResponse.failureMessage;
        } catch (e: unknown) {
            logError(`Couldn't purchase from buy button: ${e}`);
        }
        this.setState({ failureMessage });
        return screenState;
    }

    private canSubmit(): boolean {
        const { screenState, email, name, billingZip, cardIsReady } = this.state;
        if (screenState === "input") {
            // FIXME: Not all postal codes are 5 digits long. Just US ones without the - trailer.
            return cardIsReady && email.length > 0 && name.length > 0 && billingZip.length > 0;
        }
        return screenState === "success" || screenState === "failure";
    }

    private onSubmit = async () => {
        const { screenState } = this.state;
        // If we've succeeded, close the modal.
        if (screenState === "success") {
            const { onFinished } = this.props;
            if (onFinished !== undefined) {
                onFinished();
            }
            return undefined;
        }
        if (!this.canSubmit()) return undefined;
        // If we haven't succeeded, we go back to the start.
        if (screenState === "failure") {
            this.setState({ screenState: "input" });
            return undefined;
        }
        // If the card element isn't ready to be submitted then don't do anything.

        // There's a lag between Stripe capturing the payment details and us sending them
        // to the server, so we introduce an intermediate "capturing" state.
        // This preserves the Card element long enough to allow Stripe to capture payment
        // details but also shows "Checking Out" and disables the button.
        this.setState({ screenState: "capturing" });
        // FIXME: .catch() should probably throw up an "Oops" modal.
        this.onSubmitInfo()
            .then(newScreenState => this.setState({ screenState: newScreenState }))
            .catch(e => {
                logError(`Submitting buy button: ${e}`);
                this.setState({
                    failureMessage: getLocalizedString("catchallPaymentError"),
                    screenState: "failure",
                });
            });

        return undefined;
    };

    public render(): React.ReactNode {
        const { basis } = this.props;
        const { currency, withShipping, items, showStripeLogo } = basis;

        const { screenState, testMode } = this.state;
        let buttonText = "";
        let bodyContent: React.ReactNode;

        switch (screenState) {
            case "input":
            // fallthrough
            case "capturing":
                buttonText = getLocalizedString("submitOrder");
                const formDisabled = screenState === "capturing";
                const maybeShipping =
                    withShipping && getFeatureSetting("phoneNumberInBuyScreen") ? (
                        <InputWrapper area="phone" key="phone">
                            <StyledInput
                                placeholder={formatLocalizedString("optionalInputPlaceholderLabel", [
                                    getLocalizedString("phoneNumber"),
                                ])}
                                value={this.state.phone}
                                onChange={e => {
                                    this.setState({ phone: e.target.value });
                                }}
                                disabled={formDisabled}
                                type="tel"
                            />
                        </InputWrapper>
                    ) : null;

                bodyContent = (
                    <FormContainer disabled={formDisabled} testMode={testMode}>
                        <FormHeader area="method">{getLocalizedString("paymentMethod")}</FormHeader>
                        <StripeWrapper id="cardWrapper" />
                        <InputWrapper area="billzip">
                            <StyledInput
                                autoComplete={disableBrowserAutocompleteToken}
                                placeholder={getLocalizedString("zip")}
                                value={this.state.billingZip}
                                onChange={e => this.setState({ billingZip: e.target.value })}
                                ref={this.billZipRef}
                            />
                        </InputWrapper>
                        {testMode && (
                            <InfoWrapper area="testmode">
                                This screen is using Stripe's Test Mode for development purposes. You must use the{" "}
                                <LinkNewWindow href="https://stripe.com/docs/testing#cards">
                                    test payment methods
                                </LinkNewWindow>{" "}
                                instead of a real payment method. Your published app will use{" "}
                                <LinkNewWindow href="https://stripe.com/docs/keys#test-live-modes">
                                    Stripe's Live Mode
                                </LinkNewWindow>{" "}
                                and accept real payment methods.
                            </InfoWrapper>
                        )}
                        <InputWrapper area="name">
                            <StyledInput
                                autoComplete="name"
                                placeholder={getLocalizedString("name")}
                                value={this.state.name}
                                onChange={e => this.setState({ name: e.target.value })}
                                disabled={formDisabled}
                            />
                        </InputWrapper>
                        <FormHeader area="contact">{getLocalizedString("contactInformation")}</FormHeader>
                        <InputWrapper area="email">
                            <StyledInput
                                autoComplete="email"
                                placeholder={getLocalizedString("emailAddress")}
                                value={this.state.email}
                                onChange={e => this.setState({ email: e.target.value })}
                                disabled={formDisabled}
                            />
                        </InputWrapper>
                        {maybeShipping}
                        {withShipping && [
                            <FormHeader area="shipping">{getLocalizedString("shippingAddress")}</FormHeader>,

                            <InputWrapper area="add1" key="add1">
                                <StyledInput
                                    placeholder={formatLocalizedString("addressLineN", ["1"])}
                                    value={this.state.address_line1}
                                    onChange={e => this.setState({ address_line1: e.target.value })}
                                    disabled={formDisabled}
                                />
                            </InputWrapper>,
                            <InputWrapper area="add2" key="add2">
                                <StyledInput
                                    placeholder={formatLocalizedString("addressLineN", ["2"])}
                                    value={this.state.address_line2}
                                    onChange={e => this.setState({ address_line2: e.target.value })}
                                    disabled={formDisabled}
                                />
                            </InputWrapper>,
                            <InputWrapper area="city" key="city">
                                <StyledInput
                                    placeholder={getLocalizedString("city")}
                                    value={this.state.address_city}
                                    onChange={e => this.setState({ address_city: e.target.value })}
                                    disabled={formDisabled}
                                />
                            </InputWrapper>,
                            <InputWrapper area="state" key="state">
                                <StyledInput
                                    placeholder={getLocalizedString("state")}
                                    value={this.state.address_state}
                                    onChange={e => this.setState({ address_state: e.target.value })}
                                    disabled={formDisabled}
                                />
                            </InputWrapper>,
                            <InputWrapper area="zip" key="zip">
                                <StyledInput
                                    autoComplete="postal-code"
                                    placeholder={getLocalizedString("zip")}
                                    value={this.state.address_zip}
                                    onChange={e => this.setState({ address_zip: e.target.value })}
                                    disabled={formDisabled}
                                />
                            </InputWrapper>,
                            <InputWrapper area="country" key="country">
                                <StyledInput
                                    autoComplete="country-name"
                                    placeholder={getLocalizedString("country")}
                                    value={this.state.address_country}
                                    onChange={e => this.setState({ address_country: e.target.value })}
                                    disabled={formDisabled}
                                />
                            </InputWrapper>,
                        ]}
                    </FormContainer>
                );
                break;
            case "auth-confirm":
            // fallthrough
            case "submitting":
                buttonText = getLocalizedString("submitOrder");
                bodyContent = <ActivitySpinner />;
                break;
            case "failure":
                // FIXME: This isn't in the design specification. How do we actually want to do this?
                buttonText = getLocalizedString("retry");
                bodyContent = <ConfirmationMessage>{this.state.failureMessage}</ConfirmationMessage>;
                break;
            case "success":
                buttonText = getLocalizedString("done");
                bodyContent = <ConfirmationMessage>{getLocalizedString("yourOrderHasBeenPlaced")}</ConfirmationMessage>;
                break;
            case "auth-start":
                // We'd like to not show any chrome during the 3D Secure process
                // As it is, too many people suspect it's a phishing attempt.
                bodyContent = this.createStripe3DSecure();
                return <BuyScreenWrapper>{bodyContent}</BuyScreenWrapper>;
        }

        return (
            <BuyScreenWrapper>
                {items.length === 1 && (
                    <HeaderArea hasImage={items[0].image !== undefined}>
                        <Name>{items[0].name}</Name>
                        {items[0].description !== undefined && <Description>{items[0].description}</Description>}
                        {items[0].image !== undefined && <HeaderImage src={items[0].image} />}
                    </HeaderArea>
                )}

                {bodyContent}
                <Spacer />
                <ButtonArea>
                    <Price>{formatPrice(totalPriceForItems(items), currency)}</Price>
                    <Total>{getLocalizedString("total")}</Total>
                    <ButtonWrapper>
                        <Button
                            includeMargin={false}
                            title={buttonText}
                            appearance={Appearance.Filled}
                            onClick={this.onSubmit}
                            url={undefined}
                            disabled={!this.canSubmit()}
                        />
                    </ButtonWrapper>
                </ButtonArea>
                {showStripeLogo && (
                    <StripeLogo>
                        <StripeIcon />
                    </StripeLogo>
                )}
            </BuyScreenWrapper>
        );
    }
}

const BuyScreen = withTheme(BuyScreenWithTheme);
export default BuyScreen;
